<template>
  <section aria-labelledby="deployment-title">
    <div class="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-3">
      <div>
        <h2 id="deployment-title">Deployment Protection</h2>
        <p>Automatic checks before SMS and email are sent. Higher order volume alone never blocks messages.</p>
      </div>
      <button class="btn btn-green" :disabled="loading" @click="load()">Refresh</button>
    </div>
    <p v-if="error" role="alert" class="alert alert-warning">{{ error }} Protection status cannot be confirmed.</p>
    <p v-if="loading && !result" role="status">Checking protection…</p>
    <template v-if="result">
      <div class="card p-4 mb-4" role="status">
        <h3 class="h5">{{ status }}</h3>
        <p class="mb-1">Last mailer check (UTC): {{ result.state?.mailer_seen_at || 'No check received' }}</p>
        <p class="mb-1">{{ result.state?.mailer_pending || 0 }} unsent requests awaiting a connection to protection.</p>
        <p class="mb-0">Held messages remain held after a restart or rollback. Evaluation rules do not control this protection.</p>
      </div>
      <div v-if="latest" class="card p-4 mb-4">
        <h3 class="h5">Latest release · {{ latest.repository }}</h3>
        <p>{{ latest.sha.slice(0, 8) }} · Monitoring started {{ latest.created_at }} UTC</p>
        <p>{{ latest.deployed_at ? 'Deployment completed. Monitoring continues.' : 'Deployment completion has not been confirmed.' }}</p>
        <p>{{ latest.scheduler_observed ? 'A pickup scheduler run has been observed after deployment.' : 'Awaiting a confirmed pickup scheduler run after deployment.' }}</p>
        <div class="row g-3">
          <div v-for="channel in ['sms', 'mail']" :key="channel" class="col-md-6">
            <h4 class="h6">{{ channel === 'sms' ? 'SMS' : 'Email' }}</h4>
            <p>{{ count(channel, 'accepted') }} accepted · {{ count(channel, 'held') }} held · {{ count(channel, 'sending') + count(channel, 'unknown') }} outcomes unresolved</p>
          </div>
        </div>
        <details>
          <summary>Historical context — never a sending limit</summary>
          <p>Eight preceding calendar weeks, excluding the deployment day. Historical errors and missing notifications can affect these figures.</p>
          <ul><li v-for="row in latest.baseline" :key="`${row.type}-${row.event_type}`">{{ row.type }} · {{ row.event_type }}: {{ row.attempts }} attempts over {{ row.active_days }} active days.</li></ul>
        </details>
      </div>
      <p v-else>No releases have been registered yet. This does not mean a deployment has been checked.</p>
      <h3 class="h5">Recent message checks</h3>
      <p>Provider acceptance is not proof of delivery. Unresolved attempts cannot be resent from this screen.</p>
      <div class="table-responsive">
        <table class="table align-middle">
          <caption>Message checks and held requests · timestamps in UTC</caption>
          <thead><tr><th>Time</th><th>Channel / shipment</th><th>Result</th><th>Reason</th><th>Action</th></tr></thead>
          <tbody>
            <tr v-for="row in result.intents.data" :key="row.id">
              <td>{{ row.created_at }}</td>
              <td>{{ row.channel === 'sms' ? 'SMS' : 'Email' }}<br />{{ row.shipment_number || 'No shipment reference' }}</td>
              <td>{{ labels[row.status] || row.status }}</td>
              <td>{{ row.reason ? reasons[row.reason] || row.reason : 'No invalid-message evidence' }}</td>
              <td><button v-if="row.status === 'held'" class="btn btn-outline-secondary" @click="select(row)">Review</button></td>
            </tr>
            <tr v-if="!result.intents.data.length"><td colspan="5">No message checks recorded yet.</td></tr>
          </tbody>
        </table>
      </div>
      <nav aria-label="Message check pages" class="d-flex align-items-center gap-3 mb-4">
        <button class="btn btn-outline-secondary" :disabled="loading || result.intents.current_page <= 1" @click="load(result.intents.current_page - 1)">Previous</button>
        <span>Page {{ result.intents.current_page }} / {{ result.intents.last_page }}</span>
        <button class="btn btn-outline-secondary" :disabled="loading || result.intents.current_page >= result.intents.last_page" @click="load(result.intents.current_page + 1)">Next</button>
      </nav>
      <section v-if="selected" class="card p-4 mb-4" aria-labelledby="review-title">
        <h3 id="review-title">Review held {{ selected.channel === 'sms' ? 'SMS' : 'email' }}</h3>
        <p>A recheck only schedules the message when it is valid now. It is checked again before sending.</p>
        <label for="review-reason">Reason for this action (do not include recipient details)</label>
        <textarea id="review-reason" v-model="comment" class="form-control mb-3" maxlength="1000" rows="3" />
        <div class="d-flex gap-3 flex-wrap">
          <button class="btn btn-green" :disabled="saving || !comment.trim()" @click="review('recheck')">Recheck eligibility</button>
          <button class="btn btn-outline-danger" :disabled="saving || !comment.trim()" @click="review('cancel')">Cancel message</button>
          <button class="btn btn-outline-secondary" :disabled="saving" @click="selected = null">Close</button>
        </div>
        <p v-if="reviewError" role="alert">{{ reviewError }}</p>
      </section>
      <p v-if="notice" role="status">{{ notice }}</p>
      <details class="card p-4 mb-4">
        <summary>Release history</summary>
        <ul><li v-for="release in result.releases" :key="release.id">{{ release.created_at }} · {{ release.repository }} · {{ release.sha.slice(0, 8) }} · {{ release.status }}</li></ul>
      </details>
    </template>
  </section>
</template>

<script setup lang="ts">
import type { DeploymentIntent, DeploymentProtectionResponse } from '@/types/Models/DeploymentProtection'
import { computed, onMounted, onUnmounted, ref } from 'vue'
import Axios from '@/services/Axios'

const result = ref<DeploymentProtectionResponse | null>(null)
const loading = ref(false)
const error = ref('')
const selected = ref<DeploymentIntent | null>(null)
const saving = ref(false)
const comment = ref('')
const reviewError = ref('')
const notice = ref('')
const latest = computed(() => result.value?.releases[0])
const status = computed(() => {
  if (error.value) return 'Protection status unavailable'
  if (!result.value?.enabled) return 'Protection is not enabled in this environment'
  const seen = result.value.state?.mailer_seen_at
  if (!seen || Date.now() - Date.parse(`${seen.replace(' ', 'T')}Z`) > 300000) return 'Mailer check missing or stale — investigate'
  return 'Protection active · SMS and email'
})
const labels: Record<string, string> = { accepted: 'Accepted by provider', sending: 'Reserved · outcome unresolved', held: 'Held — not sent', cancelled: 'Cancelled', retry_requested: 'Recheck queued', unknown: 'Provider outcome unknown', not_sent: 'Not sent' }
const reasons: Record<string, string> = { assignment_archived: 'Assignment is archived', shipment_collected_or_returned: 'Shipment has been collected or returned', collection_deadline_passed: 'Collection deadline has passed', provider_outcome_unknown: 'Provider outcome could not be confirmed' }
function count(channel: string, state: string) { return Number(latest.value?.counts.find(row => row.channel === channel && row.status === state)?.attempts || 0) }
async function load(page = result.value?.intents.current_page || 1) {
  if (loading.value) return
  loading.value = true
  try {
    result.value = (await Axios.get<DeploymentProtectionResponse>('/deployment-protection', { params: { page } })).data
    error.value = ''
  } catch { error.value = 'Unable to read deployment protection.' }
  finally { loading.value = false }
}
function select(row: DeploymentIntent) { selected.value = row; comment.value = ''; reviewError.value = ''; notice.value = '' }
async function review(action: 'cancel' | 'recheck') {
  if (!selected.value || saving.value || !comment.value.trim()) return
  saving.value = true
  try {
    const response = await Axios.post<{ status: string }>(`/deployment-protection/${selected.value.id}/review`, { action, comment: comment.value })
    notice.value = response.data.status === 'held' ? 'Still held: the message is not currently eligible.' : response.data.status === 'cancelled' ? 'Message cancelled.' : 'Eligibility passed. The message is queued for another check before sending.'
    selected.value = null
    await load()
  } catch { reviewError.value = 'The action failed. Your comment has been kept; refresh before retrying.' }
  finally { saving.value = false }
}
let timer: ReturnType<typeof setInterval> | undefined
onMounted(() => { void load(); timer = setInterval(() => { if (!selected.value && !document.hidden) void load() }, 30000) })
onUnmounted(() => clearInterval(timer))
</script>
